import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSuperAuth } from '../../contexts/SuperAuthContext';
import {
  listTenants, createTenant, updateTenant, setTenantStatus, reprovisionTenant,
  startSupportSession, listSupportSessions, revokeSupportSession, listPlatformAudit,
  FEATURE_KEYS, TenantRow, SupportSessionRow, PlatformAuditRow, TenantStatus,
} from '../../api/super';

// ─── Platform console ────────────────────────────────────────────────────────
//
// Firms, subscriptions and support access. Deliberately shows NO business data:
// counts and status only. Reading a firm's actual records requires opening a
// time-boxed support session, which that firm can see and end.

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

const fmtDateTime = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

const STATUS_STYLES: Record<TenantStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  TRIAL: 'bg-blue-100 text-blue-800',
  SUSPENDED: 'bg-amber-100 text-amber-800',
  CANCELLED: 'bg-gray-200 text-gray-700',
};

const StatusPill: React.FC<{ status: TenantStatus }> = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}>{status}</span>
);

const errText = (e: any, fallback: string) => e?.response?.data?.message || fallback;

// ─────────────────────────────────────────────────────────────────────────────

const SuperConsole: React.FC = () => {
  const { admin, logout } = useSuperAuth();
  const [tab, setTab] = useState<'firms' | 'support' | 'audit'>('firms');

  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTenants();
      setTenants(res.data);
      setError('');
    } catch (e: any) {
      setError(errText(e, 'Could not load firms'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center text-sm">◆</span>
            <div>
              <div className="font-semibold leading-tight">Platform Console</div>
              <div className="text-xs text-slate-400 leading-tight">{admin?.name} · {admin?.email}</div>
            </div>
          </div>
          <button onClick={logout} className="text-sm text-slate-300 hover:text-white">Sign out</button>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          {([['firms', 'Firms'], ['support', 'Support Access'], ['audit', 'Audit Log']] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm rounded-t-lg ${tab === k ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-300 hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {banner && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-4 py-2 flex justify-between">
            <span>{banner}</span>
            <button onClick={() => setBanner('')} className="text-green-700">×</button>
          </div>
        )}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">{error}</div>
        )}

        {loading ? (
          <div className="text-gray-500 text-sm">Loading…</div>
        ) : tab === 'firms' ? (
          <FirmsTab tenants={tenants} refresh={refresh} notify={setBanner} onError={setError} />
        ) : tab === 'support' ? (
          <SupportTab tenants={tenants} notify={setBanner} onError={setError} />
        ) : (
          <AuditTab tenants={tenants} onError={setError} />
        )}
      </main>
    </div>
  );
};

// ── Firms ────────────────────────────────────────────────────────────────────

const FirmsTab: React.FC<{
  tenants: TenantRow[];
  refresh: () => Promise<void>;
  notify: (m: string) => void;
  onError: (m: string) => void;
}> = ({ tenants, refresh, notify, onError }) => {
  const [editing, setEditing] = useState<TenantRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [supportFor, setSupportFor] = useState<TenantRow | null>(null);

  const changeStatus = async (t: TenantRow, status: TenantStatus) => {
    // Suspension locks every user of the firm out on their next request, so it
    // gets an explicit confirmation naming the firm.
    if (status === 'SUSPENDED' || status === 'CANCELLED') {
      const reason = window.prompt(
        `Suspend "${t.name}"? Everyone at the firm is signed out immediately and cannot log back in.\n\nReason (recorded in the audit log):`,
      );
      if (reason === null) return;
      try {
        await setTenantStatus(t.id, status, reason);
        notify(`${t.name} is now ${status.toLowerCase()}.`);
        await refresh();
      } catch (e: any) { onError(errText(e, 'Could not change status')); }
      return;
    }
    try {
      await setTenantStatus(t.id, status);
      notify(`${t.name} reactivated.`);
      await refresh();
    } catch (e: any) { onError(errText(e, 'Could not change status')); }
  };

  const reprovision = async (t: TenantRow) => {
    try {
      const res = await reprovisionTenant(t.id);
      const p = res.data.provisioned;
      const added = Object.entries(p)
        .filter(([, v]) => v === true || (typeof v === 'number' && v > 0))
        .map(([k]) => k);
      notify(added.length ? `${t.name}: added ${added.join(', ')}.` : `${t.name} was already fully provisioned.`);
      await refresh();
    } catch (e: any) { onError(errText(e, 'Re-provisioning failed')); }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-slate-800">{tenants.length} firm{tenants.length === 1 ? '' : 's'}</h2>
        <button className="btn-primary !bg-slate-800 hover:!bg-slate-700" onClick={() => setCreating(true)}>
          Onboard a firm
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Firm</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Plan</th>
              <th className="text-right px-4 py-3 font-medium">Users</th>
              <th className="text-right px-4 py-3 font-medium">Clients</th>
              <th className="text-right px-4 py-3 font-medium">Invoices</th>
              <th className="text-left px-4 py-3 font-medium">Last activity</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => {
              const disabled = FEATURE_KEYS.filter((f) => t.features?.[f.key] === false);
              const atSeatLimit = t.maxUsers != null && t.usage.users >= t.maxUsers;
              return (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">/{t.slug} · since {fmtDate(t.createdAt)}</div>
                    {disabled.length > 0 && (
                      <div className="text-xs text-amber-700 mt-0.5">off: {disabled.map((f) => f.label).join(', ')}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={t.status} />
                    {t.trialEndsAt && <div className="text-xs text-slate-500 mt-0.5">trial ends {fmtDate(t.trialEndsAt)}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.plan}</td>
                  <td className={`px-4 py-3 text-right ${atSeatLimit ? 'text-amber-700 font-medium' : 'text-slate-700'}`}>
                    {t.usage.users}{t.maxUsers != null ? ` / ${t.maxUsers}` : ''}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{t.usage.clients}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{t.usage.invoices}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(t.lastActivityAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end flex-wrap">
                      <button className="text-slate-700 hover:underline" onClick={() => setEditing(t)}>Edit</button>
                      <button className="text-slate-700 hover:underline" onClick={() => setSupportFor(t)}>Support</button>
                      <button className="text-slate-700 hover:underline" onClick={() => reprovision(t)}>Re-provision</button>
                      {t.status === 'SUSPENDED' || t.status === 'CANCELLED' ? (
                        <button className="text-green-700 hover:underline" onClick={() => changeStatus(t, 'ACTIVE')}>Reactivate</button>
                      ) : (
                        <button className="text-red-700 hover:underline" onClick={() => changeStatus(t, 'SUSPENDED')}>Suspend</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditFirmModal
          tenant={editing}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => { setEditing(null); notify(msg); await refresh(); }}
          onError={onError}
        />
      )}
      {creating && (
        <CreateFirmModal
          onClose={() => setCreating(false)}
          onCreated={async (msg) => { setCreating(false); notify(msg); await refresh(); }}
        />
      )}
      {supportFor && (
        <StartSupportModal
          tenant={supportFor}
          onClose={() => setSupportFor(null)}
          onStarted={(msg) => { setSupportFor(null); notify(msg); }}
        />
      )}
    </>
  );
};

// ── Edit plan / limits / features ────────────────────────────────────────────

const EditFirmModal: React.FC<{
  tenant: TenantRow;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (m: string) => void;
}> = ({ tenant, onClose, onSaved, onError }) => {
  const [name, setName] = useState(tenant.name);
  const [plan, setPlan] = useState(tenant.plan);
  const [maxUsers, setMaxUsers] = useState(tenant.maxUsers == null ? '' : String(tenant.maxUsers));
  const [quota, setQuota] = useState(tenant.storageQuotaMb == null ? '' : String(tenant.storageQuotaMb));
  const [notes, setNotes] = useState(tenant.notes ?? '');
  const [features, setFeatures] = useState<Record<string, boolean>>(tenant.features ?? {});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await updateTenant(tenant.id, {
        name: name.trim(),
        plan,
        // Blank means "no limit", which is not the same as zero — zero would
        // lock the firm out of its own account.
        maxUsers: maxUsers.trim() === '' ? null : Number(maxUsers),
        storageQuotaMb: quota.trim() === '' ? null : Number(quota),
        notes: notes.trim() === '' ? null : notes,
        features,
      });
      onSaved(`${name} updated.`);
    } catch (e: any) {
      onError(errText(e, 'Could not save'));
      setBusy(false);
    }
  };

  const seatWarning =
    maxUsers.trim() !== '' && Number(maxUsers) < tenant.usage.users
      ? `This firm already has ${tenant.usage.users} users. Existing accounts keep working; new ones are refused until the limit is raised.`
      : '';

  return (
    <Modal title={`Edit ${tenant.name}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="label">Firm name</label>
          <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} />
          <p className="text-xs text-gray-500 mt-1">
            Shown in this console. What the firm's own clients see comes from that firm's settings, not here.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">Plan</label>
            <select className="input-field" value={plan} onChange={(e) => setPlan(e.target.value)}>
              {['TRIAL', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Max users</label>
            <input className="input-field" type="number" min={1} placeholder="unlimited"
              value={maxUsers} onChange={(e) => setMaxUsers(e.target.value)} />
          </div>
          <div>
            <label className="label">Storage (MB)</label>
            <input className="input-field" type="number" min={1} placeholder="unlimited"
              value={quota} onChange={(e) => setQuota(e.target.value)} />
          </div>
        </div>
        {seatWarning && <p className="text-xs text-amber-700">{seatWarning}</p>}

        <div>
          <label className="label">Modules</label>
          <p className="text-xs text-gray-500 mb-2">
            Unticking a module hides it from the firm immediately — the API refuses it, not just the menu.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {FEATURE_KEYS.map((f) => {
              const on = features[f.key] !== false; // absent = enabled
              return (
                <label key={f.key} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => setFeatures({ ...features, [f.key]: e.target.checked })}
                  />
                  {f.label}
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label className="label">Internal notes</label>
          <textarea className="input-field" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Billing contact, onboarding notes — never shown to the firm" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary !bg-slate-800 hover:!bg-slate-700" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Onboard a firm ───────────────────────────────────────────────────────────

const CreateFirmModal: React.FC<{ onClose: () => void; onCreated: (msg: string) => void }> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [plan, setPlan] = useState('STANDARD');
  const [maxUsers, setMaxUsers] = useState('');
  const [withAdmin, setWithAdmin] = useState(true);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Suggest a slug from the name until the operator edits it themselves.
  const autoSlug = useMemo(
    () => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40),
    [name],
  );
  const effectiveSlug = slugTouched ? slug : autoSlug;

  const submit = async () => {
    setError('');
    if (withAdmin && adminPassword.length < 12) {
      setError('The admin password must be at least 12 characters.');
      return;
    }
    setBusy(true);
    try {
      await createTenant({
        name: name.trim(),
        slug: effectiveSlug,
        plan,
        maxUsers: maxUsers.trim() === '' ? null : Number(maxUsers),
        admin: withAdmin
          ? { email: adminEmail.trim(), name: adminName.trim(), password: adminPassword }
          : undefined,
      });
      onCreated(
        withAdmin
          ? `${name} onboarded. Send ${adminEmail.trim()} their password over a separate channel — it is not emailed automatically.`
          : `${name} created. It has no login yet; add one before handing it over.`,
      );
    } catch (e: any) {
      setError(errText(e, 'Could not create the firm'));
      setBusy(false);
    }
  };

  return (
    <Modal title="Onboard a firm" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="label">Firm name</label>
          <input className="input-field" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sharma & Associates" />
        </div>
        <div>
          <label className="label">Slug</label>
          <input className="input-field" value={effectiveSlug}
            onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }} />
          <p className="text-xs text-gray-500 mt-1">
            Lowercase letters, digits and hyphens. Used to identify the firm; it cannot be changed later.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Plan</label>
            <select className="input-field" value={plan} onChange={(e) => setPlan(e.target.value)}>
              {['TRIAL', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Max users</label>
            <input className="input-field" type="number" min={1} placeholder="unlimited"
              value={maxUsers} onChange={(e) => setMaxUsers(e.target.value)} />
          </div>
        </div>

        <div className="border-t border-gray-100 pt-3">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <input type="checkbox" checked={withAdmin} onChange={(e) => setWithAdmin(e.target.checked)} />
            Create the firm's first admin login
          </label>
          <p className="text-xs text-gray-500 mt-1">
            Without one, nobody at the firm can sign in. You can add it later, but the account is unusable until you do.
          </p>
          {withAdmin && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="label">Admin name</label>
                <input className="input-field" value={adminName} onChange={(e) => setAdminName(e.target.value)} />
              </div>
              <div>
                <label className="label">Admin email</label>
                <input className="input-field" type="email" value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)} autoComplete="off" />
              </div>
              <div className="col-span-2">
                <label className="label">Temporary password</label>
                <input className="input-field" type="text" value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)} autoComplete="off"
                  placeholder="at least 12 characters" />
                <p className="text-xs text-gray-500 mt-1">
                  Shown in plain text so you can copy it. It is not emailed — pass it on separately and have them
                  change it at first sign-in.
                </p>
              </div>
            </div>
          )}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="btn-primary !bg-slate-800 hover:!bg-slate-700"
            onClick={submit}
            disabled={busy || !name.trim() || !effectiveSlug || (withAdmin && (!adminName.trim() || !adminEmail.trim()))}
          >
            {busy ? 'Creating…' : 'Create firm'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Support access ───────────────────────────────────────────────────────────

const StartSupportModal: React.FC<{
  tenant: TenantRow;
  onClose: () => void;
  onStarted: (msg: string) => void;
}> = ({ tenant, onClose, onStarted }) => {
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState(4);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState<{ token: string; expiresAt: string } | null>(null);

  const start = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await startSupportSession(tenant.id, reason.trim(), hours);
      setIssued({ token: res.data.token, expiresAt: res.data.session.expiresAt });
    } catch (e: any) {
      setError(errText(e, 'Could not start the session'));
      setBusy(false);
    }
  };

  if (issued) {
    return (
      <Modal title="Support session started" onClose={() => onStarted(`Support session opened on ${tenant.name}.`)}>
        <div className="space-y-3 text-sm">
          <p className="text-slate-700">
            Read-only access to <strong>{tenant.name}</strong> until {fmtDateTime(issued.expiresAt)}.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800 text-xs">
            {tenant.name}'s administrators can see this session, the reason you gave, and every endpoint you open —
            and they can end it at any time.
          </div>
          <div>
            <label className="label">Access token</label>
            <textarea className="input-field font-mono text-xs" rows={4} readOnly value={issued.token} />
            <p className="text-xs text-gray-500 mt-1">
              Use it as a Bearer token against the normal API. Writes are refused.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => navigator.clipboard?.writeText(issued.token)}>Copy token</button>
            <button className="btn-primary !bg-slate-800 hover:!bg-slate-700"
              onClick={() => onStarted(`Support session opened on ${tenant.name}.`)}>Done</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Request support access to ${tenant.name}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600">
          Access is read-only and time-boxed. The firm sees your name, your reason and what you opened, and can revoke
          it at any moment. Only open a session when you actually need to look at their data.
        </div>
        <div>
          <label className="label">Reason</label>
          <textarea className="input-field" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Investigating the invoice numbering issue reported in ticket #482" />
          <p className="text-xs text-gray-500 mt-1">At least 10 characters. The firm will read this.</p>
        </div>
        <div>
          <label className="label">Duration</label>
          <select className="input-field" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {[1, 2, 4, 8, 24].map((h) => <option key={h} value={h}>{h} hour{h === 1 ? '' : 's'}</option>)}
          </select>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary !bg-slate-800 hover:!bg-slate-700" onClick={start}
            disabled={busy || reason.trim().length < 10}>
            {busy ? 'Starting…' : 'Start session'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const SupportTab: React.FC<{
  tenants: TenantRow[];
  notify: (m: string) => void;
  onError: (m: string) => void;
}> = ({ tenants, notify, onError }) => {
  const [rows, setRows] = useState<SupportSessionRow[]>([]);
  const [filter, setFilter] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSupportSessions(filter === '' ? undefined : filter);
      setRows(res.data);
    } catch (e: any) { onError(errText(e, 'Could not load sessions')); }
    finally { setLoading(false); }
  }, [filter, onError]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (s: SupportSessionRow) => {
    try {
      await revokeSupportSession(s.id);
      notify('Session ended.');
      await load();
    } catch (e: any) { onError(errText(e, 'Could not end the session')); }
  };

  const isLive = (s: SupportSessionRow) => !s.revokedAt && new Date(s.expiresAt).getTime() > Date.now();

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Support access history</h2>
        <select className="input-field max-w-xs" value={filter}
          onChange={(e) => setFilter(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">All firms</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Firm</th>
              <th className="text-left px-4 py-3 font-medium">Operator</th>
              <th className="text-left px-4 py-3 font-medium">Reason</th>
              <th className="text-left px-4 py-3 font-medium">Started</th>
              <th className="text-left px-4 py-3 font-medium">Ends</th>
              <th className="text-left px-4 py-3 font-medium">State</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No support sessions yet.</td></tr>
            )}
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="px-4 py-3 text-slate-900">{s.tenant?.name}</td>
                <td className="px-4 py-3 text-slate-700">{s.superAdmin?.name}</td>
                <td className="px-4 py-3 text-slate-600 max-w-md">{s.reason}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDateTime(s.startedAt)}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDateTime(s.expiresAt)}</td>
                <td className="px-4 py-3">
                  {s.revokedAt
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">ended</span>
                    : isLive(s)
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">live</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">expired</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {isLive(s) && <button className="text-red-700 hover:underline" onClick={() => revoke(s)}>End now</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ── Audit ────────────────────────────────────────────────────────────────────

const AuditTab: React.FC<{ tenants: TenantRow[]; onError: (m: string) => void }> = ({ tenants, onError }) => {
  const [rows, setRows] = useState<PlatformAuditRow[]>([]);
  const [tenantId, setTenantId] = useState<number | ''>('');
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPlatformAudit({
        tenantId: tenantId === '' ? undefined : tenantId,
        action: action || undefined,
        limit: 300,
      });
      setRows(res.data);
    } catch (e: any) { onError(errText(e, 'Could not load the audit log')); }
    finally { setLoading(false); }
  }, [tenantId, action, onError]);

  useEffect(() => { load(); }, [load]);

  const actions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.action))).sort(),
    [rows],
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4 gap-3">
        <h2 className="text-lg font-semibold text-slate-800">Platform audit log</h2>
        <div className="flex gap-2">
          <select className="input-field" value={tenantId}
            onChange={(e) => setTenantId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">All firms</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select className="input-field" value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">When</th>
              <th className="text-left px-4 py-3 font-medium">Action</th>
              <th className="text-left px-4 py-3 font-medium">Firm</th>
              <th className="text-left px-4 py-3 font-medium">Operator</th>
              <th className="text-left px-4 py-3 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Nothing recorded yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{fmtDateTime(r.createdAt)}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.action}</td>
                <td className="px-4 py-2 text-slate-700">{r.tenant?.name ?? '—'}</td>
                <td className="px-4 py-2 text-slate-700">{r.superAdmin?.name ?? 'system'}</td>
                <td className="px-4 py-2 text-slate-500 text-xs max-w-lg break-words">
                  {r.detail ? JSON.stringify(r.detail) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ── Shared modal shell ───────────────────────────────────────────────────────

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-4 z-50 overflow-y-auto">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

export default SuperConsole;
